import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal, Signal } from '@angular/core';
import { BxBreadcrumb } from '@beyonnex/bx-angular-ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ColDef, ColGroupDef } from 'ag-grid-enterprise';
import { ROUTES } from '../../app.models';
import { DittoDatasource } from '../../components/ditto-grid/datasource/ditto-datasource';
import { DittoGridComponent } from '../../components/ditto-grid/ditto-grid.component';
import { Thing, THING_TYPE } from '../../models/thing.models';
import { DittoService } from '../../services/ditto/ditto.service';
import { DittoGridService } from '../../services/grid/ditto-grid.service';
import { GridExportService } from '../../services/grid/grid-export.service';
import { COLUMN_DEFS_STORAGE_KEY, GridPersistColumnDefsService } from '../../services/grid/grid-persist-column-defs.service';
import { GridPersistFiltersService } from '../../services/grid/grid-persist-filters.service';
import { GridPersistSortService } from '../../services/grid/grid-persist-sort.service';
import { ScreenSizeService } from '../../services/screen-size/screen-size.service';
import { Rql } from '../../utils/rql/rql';
import { PageComponent } from '../page/page.component';

@Component({
  selector: 'bxi-device-list',
  imports: [PageComponent, TranslatePipe, DittoGridComponent],
  providers: [
    GridPersistFiltersService,
    GridPersistColumnDefsService,
    { provide: COLUMN_DEFS_STORAGE_KEY, useValue: 'propertyListColumnDefs' },
    GridPersistSortService,
  ],
  templateUrl: './property-list.component.html',
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyListComponent implements OnInit, OnDestroy {
  private readonly dittoGrid = inject(DittoGridService);
  private readonly dittoService = inject(DittoService);
  private readonly exportService = inject(GridExportService);
  protected readonly persistFiltersService = inject(GridPersistFiltersService);
  protected readonly persistColumnDefsService = inject(GridPersistColumnDefsService);
  protected readonly persistSortService = inject(GridPersistSortService);
  private readonly translate = inject(TranslateService);
  private readonly screenSize = inject(ScreenSizeService);

  protected readonly columnDefs: Signal<(ColDef | ColGroupDef)[]> = signal([
    this.dittoGrid.COL_GROUP_PROPERTY_RELATED,
    this.dittoGrid.COL_GROUP_SUBMETERING_ANALYTICS,
    this.dittoGrid.COL_GROUP_DEVICE_TYPE_COUNTS,
  ]);
  protected readonly defaultColDef = computed(() =>
    this.dittoGrid.getDefaultColDefs({
      resetColumnsMenuItem: {
        name: this.translate.instant('agGrid.resetColumns'),
        action: (params): void => {
          params.api.updateGridOptions({ columnDefs: this.columnDefs() });
        },
      },
      exportAllMenuItem: {
        name: this.translate.instant('agGrid.exportAll'),
        action: (params): void => {
          this.exportService.exportAll(this.dittoDatasource(), params.api);
        },
      },
    }),
  );

  protected readonly dittoDatasource = computed(
    () =>
      new DittoDatasource(this.dittoService, this.columnDefs(), {
        baseQuery: Rql.eq(Thing.propertyPath('attributes.type', '/'), THING_TYPE.PROPERTY),
        extraFields: [Thing.propertyPath('thingId')],
        totalCountCallback: this.handleTotalCountChange.bind(this),
      }),
  );

  protected readonly breadcrumbOptions = signal<BxBreadcrumb[]>([
    { label: this.translate.instant('navigation.properties'), route: `/${ROUTES.PROPERTIES}` },
  ]);

  protected totalCount = signal<string>('');

  ngOnInit(): void {
    this.screenSize.setScreenSize('full');
  }

  ngOnDestroy(): void {
    this.screenSize.setScreenSize('regular');
  }

  protected handleTotalCountChange(totalCount: number | null): void {
    this.totalCount.set(totalCount !== null ? ` (${totalCount})` : '');
  }
}
